import { ChevronDownIcon, ListFilterIcon, PlusIcon } from 'lucide-react';

import type { SessionChargeId } from '@nesso/ledger-contracts/ids';
import { Button } from '@nesso/ui/primitives/button';
import { Item, ItemActions, ItemContent, ItemTitle } from '@nesso/ui/primitives/item';

import type { SelectedClient } from '@/entities/client';
import type { Payment } from '@/entities/payment';
import type { GetSessionChargeListOutput, SessionCharge } from '@/entities/session-charge';
import { openScheduleSession } from '@/features/session/schedule-session';
import { formatCurrencyFromCents } from '@/shared/lib/format-utils';
import { formatNumericShortDate } from '@/shared/lib/time-utils';
import { useLoadMoreOnVisible } from '@/shared/lib/use-load-more-on-visible';
import { DataSpinner } from '@/shared/ui/indicators/data-spinner';

import { ClientBillingSheetBillingDetails } from './client-billing-sheet-sessions/client-billing-sheet-billing-details';
import {
    ClientBillingSheetSessionChargesTable,
    type SessionChargeColumn,
} from './client-billing-sheet-sessions/client-billing-sheet-session-charges-table';

const unpaidColumns: SessionChargeColumn<SessionCharge>[] = [
    {
        key: 'fee',
        header: 'Fee',
        headClassName: 'text-right',
        cellClassName: 'text-right',
        cell: (sessionCharge) => formatCurrencyFromCents(sessionCharge.feeCents),
    },
    {
        key: 'balance',
        header: 'Balance',
        headClassName: 'text-right',
        cellClassName: 'text-right',
        cell: (sessionCharge) => formatCurrencyFromCents(sessionCharge.balanceCents),
    },
];

const paidColumns: SessionChargeColumn<SessionCharge>[] = [
    {
        key: 'paid',
        header: 'Paid',
        headClassName: 'w-20.75 md:w-39.25',
        cellClassName: 'text-muted-foreground',
        cell: (sessionCharge) =>
            sessionCharge.paidAt ? formatNumericShortDate(sessionCharge.paidAt) : '',
    },
    {
        key: 'fee',
        header: 'Fee',
        headClassName: 'w-20.75 text-right md:w-39.25',
        cellClassName: 'text-right',
        cell: (sessionCharge) => formatCurrencyFromCents(sessionCharge.feeCents),
    },
];

type Props = {
    client: SelectedClient | undefined;
    pages: GetSessionChargeListOutput[] | undefined;
    unpaidCount: number | undefined;
    paidCount: number | undefined;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    isFetchNextPageError: boolean;
    fetchNextPage: () => void;
    onAddPayment: (sessionChargeId: SessionChargeId | null) => void;
    onOpenPayment: (payment: Payment, sessionChargeId: SessionChargeId) => void;
    onRefund: (payment: Payment) => void;
    onEditFee: (sessionCharge: SessionCharge) => void;
};

export function ClientBillingSheetSessions({
    client,
    pages,
    unpaidCount,
    paidCount,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
    onAddPayment,
    onOpenPayment,
    onRefund,
    onEditFee,
}: Props) {
    const sentinelRef = useLoadMoreOnVisible({
        hasNextPage: hasNextPage && !isFetchNextPageError,
        isFetchingNextPage,
        fetchNextPage,
    });

    if (!pages?.[0] || unpaidCount === undefined || paidCount === undefined) {
        return (
            <Item variant="muted" alignment="center">
                <ItemContent>
                    <ItemTitle className="leading-5">Couldn't load sessions</ItemTitle>
                </ItemContent>
            </Item>
        );
    }

    const sessionCharges = pages.flatMap((page) => page.items);
    const pagePaymentsByChargeId = new Map(
        pages.flatMap((page) =>
            page.items.map((sessionCharge) => [sessionCharge.id, page.payments] as const),
        ),
    );
    const unpaidSessionCharges = sessionCharges.filter(
        (sessionCharge) => sessionCharge.status === 'UNPAID',
    );
    const paidSessionCharges = sessionCharges.filter(
        (sessionCharge) => sessionCharge.status === 'PAID',
    );

    if (unpaidCount === 0 && paidCount === 0) {
        return (
            <Item variant="muted" alignment="center">
                <ItemContent>
                    <ItemTitle className="leading-5">No sessions scheduled yet</ItemTitle>
                </ItemContent>
                <ItemActions>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => openScheduleSession(client)}
                    >
                        Schedule Session
                    </Button>
                </ItemActions>
            </Item>
        );
    }

    return (
        <div className="flex flex-col gap-6">
            <ClientBillingSheetSessionChargesTable
                label="Unpaid"
                action={
                    <Button
                        type="button"
                        size="sm"
                        disabled={unpaidCount === 0}
                        onClick={() => onAddPayment(null)}
                    >
                        <PlusIcon />
                        Payment
                    </Button>
                }
                count={unpaidCount}
                sessionCharges={unpaidSessionCharges}
                columns={unpaidColumns}
                detailsHeadClassName="w-8.5 md:w-auto"
                emptyMessage="No unpaid sessions"
                renderBillingDetails={(sessionCharge) => (
                    <ClientBillingSheetBillingDetails
                        sessionCharge={sessionCharge}
                        pagePayments={pagePaymentsByChargeId.get(sessionCharge.id) ?? []}
                        onAddPayment={onAddPayment}
                        onOpenPayment={onOpenPayment}
                        onRefund={onRefund}
                        onEditFee={onEditFee}
                    />
                )}
            />
            <ClientBillingSheetSessionChargesTable
                label="Paid"
                headerClassName="-mr-2.5"
                action={
                    <Button type="button" variant="ghost" className="has-[>svg]:px-4">
                        <ListFilterIcon />
                        All
                        <ChevronDownIcon />
                    </Button>
                }
                count={paidCount}
                sessionCharges={paidSessionCharges}
                columns={paidColumns}
                serviceHeadClassName="w-22.5 md:w-39.25"
                emptyMessage="No paid sessions yet"
                renderBillingDetails={(sessionCharge) => (
                    <ClientBillingSheetBillingDetails
                        sessionCharge={sessionCharge}
                        pagePayments={pagePaymentsByChargeId.get(sessionCharge.id) ?? []}
                        onAddPayment={onAddPayment}
                        onOpenPayment={onOpenPayment}
                        onRefund={onRefund}
                        onEditFee={onEditFee}
                    />
                )}
            />
            <div ref={sentinelRef} className="flex justify-center">
                {isFetchingNextPage && (
                    <div role="status" aria-label="Loading more sessions">
                        <DataSpinner />
                    </div>
                )}
            </div>
            {isFetchNextPageError && (
                <Item variant="muted" alignment="center">
                    <ItemContent>
                        <ItemTitle className="leading-5">Couldn't load more sessions</ItemTitle>
                    </ItemContent>
                    <ItemActions>
                        <Button type="button" variant="outline" size="sm" onClick={fetchNextPage}>
                            Retry
                        </Button>
                    </ItemActions>
                </Item>
            )}
        </div>
    );
}
